// src/hooks/useTransactions.js
import { useState, useEffect } from 'react';
import transactionService from '../services/transaction.service';
import authService from '../services/auth.service';

export const useTransactions = () => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchTransactions = async () => {
    const userId = authService.getUserId();
    if (!userId) return;

    setLoading(true);
    setError(null);
    try {
      const data = await transactionService.getUserTransactions(userId);
      
      // Transform backend data to match frontend format
      const transformedData = data.map(tx => ({
        id: tx.transactionId || tx.id,
        investmentId: tx.investmentId,
        investmentName: tx.investmentName || tx.type,
        type: tx.transactionType.toLowerCase(),
        amount: tx.amount,
        date: tx.transactionDate || tx.createdAt
      }));
      setTransactions(transformedData);
    } catch (err) {
      console.error('Error fetching transactions:', err);
      setError(err.response?.data?.message || 'Failed to fetch transactions');
    } finally {
      setLoading(false);
    }
  };

  const fetchInvestmentTransactions = async (investmentId) => {
    setLoading(true);
    setError(null);
    try {
      const data = await transactionService.getInvestmentTransactions(investmentId);
      console.log('Investment transactions:', data);
      
      // Transform backend data to match frontend format
      const transformedData = data.map(tx => ({
        id: tx.transactionId || tx.id,
        investmentId: tx.investmentId,
        investmentName: tx.investmentName || 'Unknown Investment',
        type: tx.transactionType.toLowerCase(),
        amount: tx.amount,
        date: tx.transactionDate || tx.createdAt
      }));
      
      return { success: true, data: transformedData };
    } catch (err) {
      console.error('Error fetching investment transactions:', err);
      const errorMessage = err.response?.data?.message || 'Failed to fetch investment transactions';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  const deposit = async (investmentId, amount, date, description) => {
    const userId = authService.getUserId();
    if (!userId) return { success: false, error: 'User not authenticated' };

    setLoading(true);
    setError(null);
    try {
      const response = await transactionService.deposit(userId, {
        investmentId,
        amount,
        date: date || new Date().toISOString().split('T')[0],
        description: description || 'Deposit'
      });
      await fetchTransactions(); // Refresh the list
      return { success: true, data: response };
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Deposit failed';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  const withdraw = async (investmentId, amount, date, description) => {
    const userId = authService.getUserId();
    if (!userId) return { success: false, error: 'User not authenticated' };

    setLoading(true);
    setError(null);
    try {
      const response = await transactionService.withdraw(userId, {
        investmentId,
        amount,
        date: date || new Date().toISOString().split('T')[0],
        description: description || 'Withdrawal'
      });
      await fetchTransactions(); // Refresh the list
      return { success: true, data: response };
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Withdrawal failed';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  const getInvestmentTransactions = (investmentId) => {
    return transactions.filter(tx => tx.investmentId === investmentId);
  };

  useEffect(() => {
    fetchTransactions();
  }, []);

  return {
    transactions,
    loading,
    error,
    fetchTransactions,
    fetchInvestmentTransactions,
    deposit,
    withdraw,
    getInvestmentTransactions
  };
};